import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box, Flex, Button, IconButton, Input, Table, Thead, Tbody, Tr, Th, Td, Text, useToast, FormControl, FormLabel,
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody, Portal, useDisclosure,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
} from "@chakra-ui/react";
import { FaPlus, FaEye, FaPen, FaTrashAlt } from "react-icons/fa";
import { Panel } from "../../../components/shared/Section";
import { TableSkeleton } from "../../../components/shared/LoadingBlock";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import LogoCell from "./components/LogoCell";

const API = process.env.REACT_APP_API_URL;
const RETAILER_FLAGS = ["pnp", "chk", "dis", "mak", "cli", "med", "pet"];

const FlagChip = ({ on, label, onClick }) => (
  <Button
    size="xs"
    variant="ghost"
    onClick={onClick}
    aria-pressed={on}
    minW="40px"
    fontWeight="600"
    color={on ? "signal.up" : "ink.400"}
    bg={on ? "signal.upSoft" : "ink.100"}
    _hover={{ bg: on ? "signal.upSoft" : "ink.200" }}
  >
    {label}
  </Button>
);

/**
 * Edit an org admin's sign-in — username / password / email — from the
 * Organisations row (2026-09-09). Org admins sit in no type-scoped view, so
 * this was the only screen that showed their credentials and it was read-only:
 * the master could not change the Gateway login's password at all. Posts only
 * the fields that changed, to the same endpoints the other views use.
 * PopoverContent stays inside <Portal> (table-row rule — CLAUDE.md #17).
 */
const OrgAdminSignInPopover = ({ admin, onSaved }) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const firstField = useRef(null);
  const [draft, setDraft] = useState({ username: "", userpass: "", useremail: "" });
  const [saving, setSaving] = useState(false);

  const open = () => {
    setDraft({ username: admin.username || "", userpass: admin.userpass || "", useremail: admin.useremail || "" });
    onOpen();
  };

  const handleSave = async () => {
    const changes = [
      ["username", "/admin/upd-username", draft.username.trim()],
      ["userpass", "/admin/upd-userpass", draft.userpass],
      ["useremail", "/admin/upd-useremail", draft.useremail.trim()],
    ].filter(([field, , value]) => value !== (admin[field] || ""));
    if (!changes.length) return onClose();
    if (changes.some(([field, , value]) => field !== "useremail" && !value)) {
      toast({ title: "Username and password cannot be blank", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      for (const [field, path, value] of changes) {
        const res = await axios.post(`${API}${path}`, { userid: admin.userid, [field]: value });
        if (res.data?.status === "error") throw new Error(res.data?.description || res.data?.message || `Failed to update ${field}`);
      }
      toast({ title: "Sign-in updated", status: "success", duration: 2500, isClosable: true });
      onClose();
      if (onSaved) onSaved();
    } catch (error) {
      toast({ title: "Update failed", description: error?.response?.data?.description || error?.response?.data?.message || error.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover isOpen={isOpen} onOpen={open} onClose={onClose} initialFocusRef={firstField} placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Button size="xs" variant="ghost" px={1} minW="auto" aria-label={`Edit sign-in for ${admin.username}`} title="Edit sign-in">
          <FaPen />
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="300px">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontSize="sm" fontWeight="600">Org admin sign-in — {admin.fname || admin.username}</PopoverHeader>
          <PopoverBody>
            <FormControl mb={2}>
              <FormLabel fontSize="xs" mb={1}>Username</FormLabel>
              <Input ref={firstField} size="sm" value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} />
            </FormControl>
            <FormControl mb={2}>
              <FormLabel fontSize="xs" mb={1}>Password</FormLabel>
              <Input size="sm" value={draft.userpass} onChange={(e) => setDraft({ ...draft, userpass: e.target.value })} />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="xs" mb={1}>Email</FormLabel>
              <Input size="sm" type="email" value={draft.useremail} onChange={(e) => setDraft({ ...draft, useremail: e.target.value })} />
            </FormControl>
            <Flex justify="flex-end">
              <Button size="sm" isLoading={saving} onClick={handleSave}>Save</Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

/**
 * The Organisations view of the Users page (spec §4.3) — everything the old
 * standalone Organisations screen did (entitlement/active toggles, create,
 * org logo) plus: the org admin's sign-in creds, member/supplier counts, and a
 * View drawer listing the organisation's users and suppliers.
 */
// A guest supplier's retailers IN ONE ORGANISATION (2026-10-07 — backend database/orgGuestRetailers.js).
// Only the retailers the supplier sells at AND the organisation has are offered; switching one changes this
// organisation only — never the supplier's own list, its home organisation or any other organisation.
const GuestRetailers = ({ org }) => {
  const toast = useToast();
  const [guests, setGuests] = useState(null);
  const load = () =>
    axios.post(`${API}/admin/get-org-guest-retailers`, { org_id: org.org_id })
      .then((r) => setGuests(r.data?.data?.guests || []))
      .catch(() => setGuests([]));
  useEffect(() => { setGuests(null); load(); }, [org.org_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const offered = (g) => RETAILER_FLAGS.filter((f) => g.supplier[f] && org[f]);
  const isOn = (g, f) => (g.override ? !!g.override[f] : true);
  const save = async (g, body) => {
    try {
      const r = await axios.post(`${API}/admin/upd-org-guest-retailers`, { org_id: org.org_id, suppliercode: g.suppliercode, ...body });
      setGuests(r.data?.data || []);
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true });
    }
  };
  const toggle = (g, f) => {
    const retailers = {};
    offered(g).forEach((x) => { retailers[x] = x === f ? !isOn(g, x) : isOn(g, x); });
    save(g, { retailers });
  };
  if (guests === null) return <Text fontSize="xs" color="ink.400">loading…</Text>;
  if (!guests.length) return null;
  return (
    <Box mb={2}>
      <Text fontWeight="600" fontSize="sm" mb={1}>Guest suppliers — retailers shown in {org.org_code}</Text>
      <Text fontSize="xs" color="ink.500" mb={2}>Only for this organisation. The supplier's own retailers and every other organisation stay as they are.</Text>
      {guests.map((g) => (
        <Box key={g.suppliercode} py={1.5} borderBottom="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={1}>
            <Text fontSize="xs" fontWeight="600">{g.label} <Text as="span" fontWeight="400" color="ink.500">· home {g.home}</Text></Text>
            {g.override && <Button size="xs" variant="link" colorScheme="gray" onClick={() => save(g, { reset: true })}>Reset</Button>}
          </Flex>
          <Flex gap={1} wrap="wrap">
            {offered(g).length === 0 && <Text fontSize="xs" color="ink.400">no retailer in common</Text>}
            {offered(g).map((f) => <FlagChip key={f} on={isOn(g, f)} label={f.toUpperCase()} onClick={() => toggle(g, f)} />)}
          </Flex>
        </Box>
      ))}
    </Box>
  );
};

const OrganisationsView = ({ allUsers, allSuppliers, search, onManageAdmins, onChanged }) => {
  const toast = useToast();
  const { fetchOrganisations, selectedOrg, setSelectedOrg } = useSupplierRetailer();

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewOrg, setViewOrg] = useState(null); // org row for the drawer

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/get-organisations`, {});
      setOrgs(res.data?.data || []);
    } catch (error) {
      toast({ title: "Failed to load organisations", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrg = async (org) => {
    try {
      const retailers = RETAILER_FLAGS.reduce((acc, f) => ({ ...acc, [f]: !!org[f] }), {});
      await axios.post(`${API}/admin/upd-organisation`, {
        org_id: org.org_id,
        org_name: org.org_name,
        contact_email: org.contact_email,
        retailers,
        activeyn: org.activeyn,
      });
      fetchOrganisations(); // keep the top-bar toggle in step
    } catch (error) {
      toast({ title: "Failed to update organisation", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
      load(); // resync after a failed optimistic toggle
    }
  };

  const toggleFlag = (org, flag) => {
    const updated = { ...org, [flag]: !org[flag] };
    setOrgs((prev) => prev.map((o) => (o.org_id === org.org_id ? updated : o)));
    saveOrg(updated);
  };

  // Delete a partner organisation (2026-09-17). The backend refuses while any
  // supplier still calls it home (move them from Users → Suppliers → Org first)
  // and removes the org's own admins + Basic users with it; SPEARMGMT never.
  const deleteOrg = async (org) => {
    const admins = Array.isArray(org.org_admins) ? org.org_admins.length : 0;
    const lines = [`Delete organisation ${org.org_name} (${org.org_code})?`];
    if (admins || org.member_count) lines.push(`This also removes its ${admins} org admin(s) and ${org.member_count || 0} Basic user(s).`);
    if (org.supplier_count) lines.push("If a supplier still calls this organisation home the delete is refused — move it first (Users → Suppliers → Org).");
    // eslint-disable-next-line no-alert
    if (!window.confirm(lines.join("\n"))) return;
    try {
      const res = await axios.post(`${API}/admin/del-organisation`, { org_id: org.org_id });
      toast({ title: res.data?.title || "Organisation deleted", description: res.data?.message, status: "success", duration: 6000, isClosable: true });
      if (selectedOrg === org.org_code) setSelectedOrg("SPEARMGMT");
      fetchOrganisations();
      load();
      if (onChanged) onChanged();
    } catch (error) {
      toast({ title: "Cannot delete organisation", description: error?.response?.data?.message || "Technical error", status: "error", duration: 9000, isClosable: true });
    }
  };

  const q = (search || "").trim().toLowerCase();
  const rows = orgs
    .filter((o) => !q || `${o.org_code} ${o.org_name} ${o.contact_email || ""}`.toLowerCase().includes(q))
    .sort((a, b) => String(a.org_name || a.org_code).localeCompare(String(b.org_name || b.org_code), undefined, { sensitivity: "base" }));

  const orgMembers = (org) => (allUsers || []).filter((u) => u.org_code === org.org_code);
  // Membership (2026-09-17): the org's own suppliers plus the ones a master made
  // visible here from another organisation (`org_codes`, home first). A guest
  // is shown with its home org so the master can tell the two apart.
  const orgSuppliers = (org) =>
    (allSuppliers || [])
      .filter((s) => (Array.isArray(s.org_codes) && s.org_codes.length ? s.org_codes : [s.org_code]).includes(org.org_code))
      .map((s) => ({ code: s.suppliercode, home: s.org_code, guest: !!s.org_code && s.org_code !== org.org_code }));

  return (
    <>
      <Panel padded={false} maxH="calc(100vh - 260px)" overflow="auto">
        {loading ? (
          <TableSkeleton rows={4} minH="160px" />
        ) : (
          <Table variant="striped" size="sm" colorScheme="custom">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th px={2} py={1} bg="gray.50">Logo</Th>
                <Th px={2} py={1} bg="gray.50">Code</Th>
                <Th px={2} py={1} bg="gray.50">Name</Th>
                <Th px={2} py={1} bg="gray.50">Org admin sign-in</Th>
                <Th px={2} py={1} bg="gray.50">Users</Th>
                <Th px={2} py={1} bg="gray.50">Suppliers</Th>
                <Th px={2} py={1} bg="gray.50">Retailers</Th>
                <Th px={2} py={1} bg="gray.50">Active</Th>
                <Th px={2} py={1} bg="gray.50">View</Th>
                <Th px={2} py={1} bg="gray.50"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((org) => {
                const admins = Array.isArray(org.org_admins) ? org.org_admins : [];
                return (
                  <Tr key={org.org_id}>
                    <Td px={2} py={1}>
                      <LogoCell storedCode={`ORG_${org.org_code}`} label={org.org_code} />
                    </Td>
                    <Td px={2} py={1} fontWeight="600">{org.org_code}</Td>
                    <Td px={2} py={1}>{org.org_name}</Td>
                    <Td px={2} py={1}>
                      {admins.length === 0 ? (
                        <Text fontSize="xs" color="ink.400">no org admin</Text>
                      ) : (
                        <Box>
                          {admins.map((admin) => (
                            <Flex key={admin.userid} align="center" gap={1}>
                              <Text fontSize="xs" whiteSpace="nowrap">
                                {admin.username} / {admin.userpass || "(invite pending)"}
                              </Text>
                              <OrgAdminSignInPopover admin={admin} onSaved={load} />
                            </Flex>
                          ))}
                          {/* Grants, sign-in toggle, invite + delete live in the Org admins view (2026-09-16). */}
                          {onManageAdmins && (
                            <Button size="xs" variant="link" colorScheme="teal" onClick={onManageAdmins}>
                              Manage in Org admins
                            </Button>
                          )}
                        </Box>
                      )}
                    </Td>
                    <Td px={2} py={1}>{org.member_count}</Td>
                    <Td px={2} py={1}>{org.supplier_count}</Td>
                    <Td px={2} py={1}>
                      <Flex gap={1} wrap="wrap">
                        {RETAILER_FLAGS.map((f) => (
                          <FlagChip key={f} on={!!org[f]} label={f.toUpperCase()} onClick={() => toggleFlag(org, f)} />
                        ))}
                      </Flex>
                    </Td>
                    <Td px={2} py={1}>
                      <FlagChip on={!!org.activeyn} label={org.activeyn ? "Yes" : "No"} onClick={() => toggleFlag(org, "activeyn")} />
                    </Td>
                    <Td px={2} py={1}>
                      <Button size="xs" variant="ghost" leftIcon={<FaEye />} onClick={() => setViewOrg(org)}>View</Button>
                    </Td>
                    <Td px={2} py={1}>
                      {org.org_code !== "SPEARMGMT" && (
                        <IconButton size="xs" variant="ghost" color="signal.down" icon={<FaTrashAlt />} aria-label={`Delete organisation ${org.org_code}`} title="Delete organisation" onClick={() => deleteOrg(org)} />
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Panel>

      <Drawer isOpen={!!viewOrg} placement="right" size="sm" onClose={() => setViewOrg(null)}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md">{viewOrg?.org_name} ({viewOrg?.org_code})</DrawerHeader>
          <DrawerBody>
            {viewOrg && (
              <>
                <GuestRetailers org={viewOrg} />
                <Text fontWeight="600" fontSize="sm" mt={4} mb={1}>Users</Text>
                {orgMembers(viewOrg).length === 0 && <Text fontSize="xs" color="ink.400" mb={2}>none</Text>}
                {orgMembers(viewOrg).map((u) => (
                  <Flex key={u.userid} justify="space-between" fontSize="xs" py={0.5} borderBottom="1px solid" borderColor="gray.100">
                    <Text>{u.fname}</Text>
                    <Text color="ink.500">{u.user_type === "SME" ? "Basic" : u.user_type} · {u.username}</Text>
                  </Flex>
                ))}
                <Text fontWeight="600" fontSize="sm" mt={4} mb={1}>Suppliers</Text>
                {orgSuppliers(viewOrg).length === 0 && <Text fontSize="xs" color="ink.400">none</Text>}
                {orgSuppliers(viewOrg).map((s) => (
                  <Flex key={s.code} justify="space-between" fontSize="xs" py={0.5} borderBottom="1px solid" borderColor="gray.100">
                    <Text>{s.code}</Text>
                    {s.guest && <Text color="ink.500">also visible here · home {s.home}</Text>}
                  </Flex>
                ))}
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

/** "Create organisation" as a popover off the toolbar (spec §4.3). */
export const CreateOrgPopover = ({ onCreated }) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newOrg, setNewOrg] = useState({ org_code: "", org_name: "", contact_email: "" });
  const [newFlags, setNewFlags] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { fetchOrganisations } = useSupplierRetailer();

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/ins-organisation`, {
        org_code: newOrg.org_code,
        org_name: newOrg.org_name,
        contact_email: newOrg.contact_email,
        retailers: newFlags,
      });
      toast({ title: res.data.message, status: "success", duration: 3000, isClosable: true });
      setNewOrg({ org_code: "", org_name: "", contact_email: "" });
      setNewFlags({});
      fetchOrganisations();
      onClose();
      if (onCreated) onCreated();
    } catch (error) {
      toast({ title: "Failed to create organisation", description: error?.response?.data?.message, status: "error", duration: 8000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Button leftIcon={<FaPlus />}>Create organisation</Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="360px">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontSize="sm" fontWeight="600">New organisation</PopoverHeader>
          <PopoverBody>
            <FormControl isRequired mb={2}>
              <FormLabel fontSize="xs" mb={1}>Code</FormLabel>
              <Input size="sm" value={newOrg.org_code} onChange={(e) => setNewOrg({ ...newOrg, org_code: e.target.value.toUpperCase() })} placeholder="ie: GATEWAY" />
            </FormControl>
            <FormControl isRequired mb={2}>
              <FormLabel fontSize="xs" mb={1}>Name</FormLabel>
              <Input size="sm" value={newOrg.org_name} onChange={(e) => setNewOrg({ ...newOrg, org_name: e.target.value })} placeholder="ie: Gateway" />
            </FormControl>
            <FormControl mb={2}>
              <FormLabel fontSize="xs" mb={1}>Contact email</FormLabel>
              <Input size="sm" type="email" value={newOrg.contact_email} onChange={(e) => setNewOrg({ ...newOrg, contact_email: e.target.value })} placeholder="optional" />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="xs" mb={1}>Retailers</FormLabel>
              <Flex gap={1} wrap="wrap">
                {RETAILER_FLAGS.map((f) => (
                  <FlagChip key={f} on={!!newFlags[f]} label={f.toUpperCase()} onClick={() => setNewFlags((prev) => ({ ...prev, [f]: !prev[f] }))} />
                ))}
              </Flex>
            </FormControl>
            <Flex justify="flex-end">
              <Button size="sm" leftIcon={<FaPlus />} isLoading={submitting} onClick={handleCreate}>Create</Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default OrganisationsView;
